import React, { useState, useMemo } from "react";
import {
  FormGroup,
  FormControlLabel,
  Checkbox,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
  Box,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import BWITextField from "../shared/BWITextField";
import { useSession } from "../../context/SessionContext";

interface SystemAccessSelectorProps {
  selectedSystems: string[];
  onSystemSelect: (systemCode: string) => void;
  disabled?: boolean;
}

const SystemAccessSelector: React.FC<SystemAccessSelectorProps> = ({
  selectedSystems,
  onSystemSelect,
  disabled
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { systems } = useSession()

  // Fuzzy search filter logic for both systemCode & commonName
  const filteredSystems = useMemo(() => {
    const filtered = systems?.filter(
      (system) =>
        system.systemCode.toLowerCase().includes(searchQuery.toLowerCase()) ||
        system.commonName.toLowerCase().includes(searchQuery.toLowerCase())
    ) || [];

    return filtered.sort((a, b) => a.commonName.localeCompare(b.commonName));
  }, [searchQuery, systems]);


  return (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <BWITextField
        label="Search System Access"
        fullWidth
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 2, flexShrink: 0 }}
        startIcon={<SearchIcon />}
        size={isMobile ? 'small' : 'medium'}
      />

      <Box sx={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto'
      }}>
        <FormGroup>
          {filteredSystems.map((system) => (
            <FormControlLabel
              key={system.systemCode}
              control={
                <Checkbox
                  checked={selectedSystems.includes(system.systemCode)}
                  onChange={() => !disabled && onSystemSelect(system.systemCode)}
                  disabled={disabled}
                  sx={{
                    "&.Mui-checked": {
                      color: "#d32f2f",
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2">
                  <strong>{system.commonName}</strong> ({system.systemCode})
                </Typography>
              }
            />
          ))}
        </FormGroup>
      </Box>
    </Paper>
  );
};

export default SystemAccessSelector;
